import { MAX_PRICE } from '@/config/consts';
import { z } from 'zod';

// Zod схемы для валидации

// Схема для PropertyTypes
export const PropertyTypeSchema = z.enum([
  'Undefined',
  'HOTEL',
  'APARTMENT',
  'HOSTEL',
  'GUEST_HOUSE',
  'VILLA',
  'RESORT',
  'B&B',
  'MOTEL',
  'COTTAGE',
  'CABIN',
  'HOLIDAY_PARK',
  'CAMPING',
  'CRUISE',
  'RYOKAN',
  'RIAD',
]);

// Схема для MealTypes
export const MealTypeSchema = z.enum([
  'Undefined',
  'RO',
  'BF',
  'BB',
  'HB',
  'FB',
  'AI',
  'UAI',
  'Custom',
  'BBF', // Завтрак «Шведский стол»
  'CBF', // Завтрак «Континентальный»
  'EBF', // Завтрак «Английский»
  'LU', // Обед
  'DI', // Ужин
  'FBB', // Полный пансион «Шведский стол»
  'FBC', // Полный пансион «Заказное меню»
  'BD', // Ужин «Шведский стол»
  'DC', // Ужин «Заказное меню»
  'CU', // Свой тип питания
  'HBL', // Полупансион: завтрак и обед
  'HBD', // Полупансион: завтрак и ужин
  'ABF', // Завтрак «Американский»
  'HBLD', // Полупансион: обед и ужин
]);

export const BasicFiltersSchema = z.object({
  regionId: z.coerce.string().nonempty(),
  regionName: z.string().nonempty(),
  cityId: z.coerce.string().nonempty(),
  cityName: z.string().nonempty(),
  timeZone: z.string().nonempty(),
  arrivalDate: z.string(),
  departureDate: z.string(),
  adults: z.number().int().min(1),
  childAges: z.array(z.number().int()).optional(),
});

export const SortSchema = z.object({
  field: z.string().optional(),
  ascending: z.boolean().optional(),
});

export const AdditionalFiltersSchema = z.object({
  minPrice: z.number().int().min(0).optional(),
  maxPrice: z.number().int().max(MAX_PRICE).optional(),
  stars: z.array(z.number().int()).optional(),
  mealTypes: z.array(MealTypeSchema).optional(),
  accommodationTypes: z.array(PropertyTypeSchema).optional(),
});

export const SearchMessageSchema = z.object({
  filters: BasicFiltersSchema.merge(AdditionalFiltersSchema),
  sort: z.array(SortSchema).default([{ field: 'price', ascending: true }]),
  start: z.number().int().min(0).optional(),
  size: z.number().int().min(1).optional(),
});

// Схема для OperationTypes
export const OperationTypeSchema = z.enum([
  'ANSWER',
  'CLEAN',
  'INSERT',
  'MOVE',
  'UPDATE',
  'DELETE',
]);

// Схема для ListingImageDto
export const ListingImageDtoSchema = z.object({
  url: z.string().url('URL должен быть валидным'),
});

// Схема для ListingAddressDto
export const ListingAddressDtoSchema = z.object({
  countryCode: z.string().min(1, 'Код страны обязателен'),
  postCode: z.string().min(1, 'Почтовый индекс обязателен'),
  region: z.string().min(1, 'Регион обязателен').nullable(),
  regionId: z.string().min(1, 'ID региона обязателен').nullable(),
  addressLine: z.string().min(1, 'Адресная строка обязательна'),
  cityId: z.string().min(1, 'ID города обязателен'),
  cityName: z.string().min(1, 'Название города обязательно'),
  latitude: z.number().min(-90).max(90, 'Широта должна быть от -90 до 90'),
  longitude: z.number().min(-180).max(180, 'Долгота должна быть от -180 до 180'),
});

// Схема для ListingItemDto
export const ListingItemDtoSchema = z.object({
  id: z.string().min(1, 'ID обязателен'),
  externalId: z.string().min(1, 'Внешний ID обязателен'),
  holderId: z.string().min(1, 'ID владельца обязателен'),
  externalHolderId: z.string().min(1, 'Внешний ID владельца обязателен'),
  systemId: z.string().min(1, 'ID системы обязателен'),
  name: z.string().min(1, 'Название обязательно'),
  address: ListingAddressDtoSchema,
  lowestPrice: z.number().nonnegative('Цена не может быть отрицательной'),
  images: z.array(ListingImageDtoSchema).min(1, 'Требуется хотя бы одно изображение'),
  propertyType: PropertyTypeSchema,
  stars: z.number().int().min(0).max(5, 'Количество звёзд от 0 до 5'),
  rating: z.number().min(0).max(10, 'Рейтинг от 0 до 10'),
  mealTypes: z.array(MealTypeSchema).min(1, 'Требуется хотя бы один тип питания'),
});

// Схема для OperationItem
export const OperationItemSchema = z.object({
  position: z.number().nullable(),
  toPosition: z.number().nullable(),
  fromPosition: z.number().nullable(),
  item: ListingItemDtoSchema.nullable(),
});

// Схема для Operation
export const OperationSchema = z.object({
  type: OperationTypeSchema,
  item: OperationItemSchema,
});

// Схема для OperationsResponseDto
export const OperationsResponseDtoSchema = z.object({
  sessionId: z.string().uuid(),
  searchKey: z.string(),
  operations: z.array(OperationSchema),
  status: z.object({
    code: z.number().int().min(100).max(599),
    text: z.string(),
  }),
  error: z
    .object({
      text: z.string(),
      errors: z.array(
        z.object({
          key: z.string(),
          value: z.string(),
        }),
      ),
    })
    .nullable(),
});

// Экспорт выведенных типов для использования в TypeScript
export type AdditionalFiltersType = z.infer<typeof AdditionalFiltersSchema>;
export type Sort = z.infer<typeof SortSchema>;
export type PropertyType = z.infer<typeof PropertyTypeSchema>;
export type MealType = z.infer<typeof MealTypeSchema>;
export type OperationType = z.infer<typeof OperationTypeSchema>;
export type ListingImageDto = z.infer<typeof ListingImageDtoSchema>;
export type ListingAddressDto = z.infer<typeof ListingAddressDtoSchema>;
export type ListingItemDto = z.infer<typeof ListingItemDtoSchema>;

export type SearchMessage = z.infer<typeof SearchMessageSchema>;
export type OperationItem = z.infer<typeof OperationItemSchema>;
export type Operation = z.infer<typeof OperationSchema>;
export type OperationsResponseDto = z.infer<typeof OperationsResponseDtoSchema>;
